import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Shield, Plus, Edit, Trash2, MoreVertical, Loader2, Key, Settings, Lock, Unlock, CheckSquare, Square, Archive, RotateCcw, Eye, EyeOff, GitMerge, ArrowLeftRight, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, getRoleLabel } from '../lib/utils';

// Permission labels in Spanish - SINCRONIZADOS CON BACKEND
const permissionLabels = {
  // Dashboard
  view_dashboard: 'Ver Dashboard',
  view_dashboard_kpis: 'Ver KPIs',
  view_dashboard_charts: 'Ver Gráficas',
  export_dashboard: 'Exportar Dashboard',
  customize_dashboard: 'Personalizar Dashboard',
  // Inventario
  view_inventory: 'Ver Inventario',
  view_inventory_detail: 'Ver Detalle de Equipo',
  create_inventory: 'Crear Equipo',
  edit_inventory: 'Editar Equipo',
  delete_inventory: 'Eliminar Equipo',
  restore_inventory: 'Restaurar Equipo Eliminado',
  bulk_delete_inventory: 'Eliminación Masiva',
  change_inventory_status: 'Cambiar Estado',
  view_inventory_history: 'Ver Historial',
  import_inventory: 'Importar',
  export_inventory: 'Exportar',
  reconcile_inventory: 'Reconciliar',
  // Categorías
  view_categories: 'Ver Categorías',
  create_categories: 'Crear Categorías',
  edit_categories: 'Editar Categorías',
  delete_categories: 'Eliminar Categorías',
  // Ubicaciones
  view_locations: 'Ver Ubicaciones',
  create_locations: 'Crear Ubicaciones',
  edit_locations: 'Editar Ubicaciones',
  delete_locations: 'Eliminar Ubicaciones',
  // Responsables
  view_responsibles: 'Ver Responsables',
  create_responsibles: 'Crear Responsables',
  edit_responsibles: 'Editar Responsables',
  delete_responsibles: 'Eliminar Responsables',
  reset_responsible_password: 'Resetear Contraseña',
  // Movimientos
  view_movements: 'Ver Movimientos',
  view_movement_detail: 'Ver Detalle',
  create_event_checkout: 'Crear Salida (Evento)',
  create_loan_checkout: 'Crear Préstamo',
  create_return: 'Registrar Retorno',
  checkout_without_review: 'Salida Sin Revisión (Bomberazo)',
  use_emergency_mode: 'Usar Modo Emergencia',
  edit_movements: 'Editar Movimientos',
  delete_movements: 'Eliminar Movimientos',
  approve_movements: 'Aprobar (Override)',
  restore_movements: 'Restaurar Eliminados',
  download_movement_pdf: 'Descargar PDF',
  // Préstamos (legacy)
  view_loans: 'Ver Préstamos',
  create_loans: 'Crear Préstamos',
  edit_loans: 'Editar Préstamos',
  // Rastreo de Equipo
  view_equipment_tracking: 'Ver Rastreo',
  view_equipment_at_events: 'Ver en Eventos',
  view_equipment_on_loan: 'Ver en Préstamo',
  view_overdue_equipment: 'Ver Vencidos',
  configure_loan_alerts: 'Configurar Alertas',
  manage_loan_reminders: 'Gestionar Recordatorios',
  // Calendario
  view_calendar: 'Ver Calendario',
  create_calendar_events: 'Crear Eventos',
  edit_calendar: 'Editar Eventos',
  delete_calendar_events: 'Eliminar Eventos',
  // Reportes
  view_reports: 'Ver Reportes',
  generate_reports: 'Generar Reportes',
  export_reports_excel: 'Exportar Excel',
  export_reports_pdf: 'Exportar PDF',
  // Usuarios
  view_users: 'Ver Usuarios',
  create_users: 'Crear Usuarios',
  edit_users: 'Editar Usuarios',
  delete_users: 'Eliminar Usuarios',
  change_user_role: 'Cambiar Rol',
  manage_user_permissions: 'Asignar Permisos',
  // Permisos de Roles
  manage_permissions: 'Gestionar Permisos',
  view_role_permissions: 'Ver Permisos de Roles',
  edit_role_permissions: 'Editar Permisos de Roles',
  create_custom_roles: 'Crear Roles Personalizados',
  // Auditoría y Debugger
  view_audit: 'Ver Auditoría',
  export_audit: 'Exportar Auditoría',
  view_debugger: 'Ver Debugger',
  manage_system_logs: 'Gestionar Logs del Sistema',
  // Backup y Sistema
  view_backup: 'Ver Backups',
  create_backup: 'Crear Backup',
  restore_backup: 'Restaurar Backup',
  manage_onedrive: 'Gestionar OneDrive',
  // Correos y Comunicaciones
  view_email_config: 'Ver Config. Correo',
  send_emails: 'Enviar Correos',
  view_announcements: 'Ver Anuncios',
  create_announcements: 'Crear Anuncios',
  edit_announcements: 'Editar Anuncios',
  delete_announcements: 'Eliminar Anuncios',
  manage_announcements: 'Gestión Completa de Anuncios',
  // Estilo y Branding
  view_style: 'Ver Estilo',
  edit_style_colors: 'Editar Colores',
  edit_style_typography: 'Editar Tipografía',
  edit_style_login: 'Editar Login',
  manage_style: 'Gestión Completa de Estilo',
  // Exportar/Importar
  export_data: 'Exportar Datos',
  import_data: 'Importar Datos',
  // Checklists
  view_checklists: 'Ver Checklists',
  manage_checklists: 'Gestionar Checklists',
  // Responsivas
  view_responsivas: 'Ver Responsivas',
  download_responsivas: 'Descargar Responsivas',
  regenerate_responsivas: 'Regenerar Responsivas',
  delete_responsivas: 'Eliminar Responsivas',
  // Plantillas
  view_templates: 'Ver Plantillas',
  edit_templates: 'Editar Plantillas'
};

// Group permissions by category - SINCRONIZADOS CON BACKEND
const permissionGroups = {
  'Dashboard': ['view_dashboard', 'view_dashboard_kpis', 'view_dashboard_charts', 'export_dashboard', 'customize_dashboard'],
  'Inventario': ['view_inventory', 'view_inventory_detail', 'create_inventory', 'edit_inventory', 'delete_inventory', 'restore_inventory', 'bulk_delete_inventory', 'change_inventory_status', 'view_inventory_history', 'import_inventory', 'export_inventory', 'reconcile_inventory'],
  'Categorías': ['view_categories', 'create_categories', 'edit_categories', 'delete_categories'],
  'Ubicaciones': ['view_locations', 'create_locations', 'edit_locations', 'delete_locations'],
  'Responsables': ['view_responsibles', 'create_responsibles', 'edit_responsibles', 'delete_responsibles', 'reset_responsible_password'],
  'Movimientos': ['view_movements', 'view_movement_detail', 'create_event_checkout', 'create_loan_checkout', 'create_return', 'checkout_without_review', 'use_emergency_mode', 'edit_movements', 'delete_movements', 'approve_movements', 'restore_movements', 'download_movement_pdf'],
  'Préstamos (Legacy)': ['view_loans', 'create_loans', 'edit_loans'],
  'Rastreo de Equipo': ['view_equipment_tracking', 'view_equipment_at_events', 'view_equipment_on_loan', 'view_overdue_equipment', 'configure_loan_alerts', 'manage_loan_reminders'],
  'Calendario': ['view_calendar', 'create_calendar_events', 'edit_calendar', 'delete_calendar_events'],
  'Reportes': ['view_reports', 'generate_reports', 'export_reports_excel', 'export_reports_pdf'],
  'Usuarios': ['view_users', 'create_users', 'edit_users', 'delete_users', 'change_user_role', 'manage_user_permissions'],
  'Permisos de Roles': ['manage_permissions', 'view_role_permissions', 'edit_role_permissions', 'create_custom_roles'],
  'Auditoría y Sistema': ['view_audit', 'export_audit', 'view_debugger', 'manage_system_logs'],
  'Backup': ['view_backup', 'create_backup', 'restore_backup', 'manage_onedrive'],
  'Comunicaciones': ['view_email_config', 'send_emails', 'view_announcements', 'create_announcements', 'edit_announcements', 'delete_announcements', 'manage_announcements'],
  'Estilo y Branding': ['view_style', 'edit_style_colors', 'edit_style_typography', 'edit_style_login', 'manage_style'],
  'Plantillas': ['view_templates', 'edit_templates'],
  'Responsivas': ['view_responsivas', 'download_responsivas', 'regenerate_responsivas', 'delete_responsivas'],
  'Datos': ['export_data', 'import_data', 'view_checklists', 'manage_checklists']
};

const UsersPage = () => {
  const { api, user: currentUser, isSuperAdmin, hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [allPermissions, setAllPermissions] = useState([]);
  const [defaultPermissions, setDefaultPermissions] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Permission check for bulk delete
  const canBulkDelete = hasPermission('bulk_delete_users') || isSuperAdmin();
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Bulk selection state
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // v5.4.0 — Merge users modal state (superadmin only)
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeUserA, setMergeUserA] = useState('');  // candidate A id
  const [mergeUserB, setMergeUserB] = useState('');  // candidate B id
  const [mergePrimary, setMergePrimary] = useState('a');  // 'a' or 'b' — which becomes primary
  const [mergePreview, setMergePreview] = useState(null);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeConfirmText, setMergeConfirmText] = useState('');
  const [mergeExecuting, setMergeExecuting] = useState(false);

  // v5.4.19 — Merge candidates (auto-suggested pairs)
  const [showCandidatesPanel, setShowCandidatesPanel] = useState(false);
  const [mergeCandidates, setMergeCandidates] = useState(null);
  const [candidatesLoading, setCandidatesLoading] = useState(false);

  const loadMergeCandidates = async () => {
    setCandidatesLoading(true);
    setShowCandidatesPanel(true);
    try {
      const res = await api().get('/users/merge/candidates');
      setMergeCandidates(res.data.candidates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron cargar los candidatos');
      setMergeCandidates([]);
    } finally {
      setCandidatesLoading(false);
    }
  };

  const pickCandidate = (c) => {
    setMergeUserA(c.user_a.id);
    setMergeUserB(c.user_b.id);
    setMergePrimary('a');
    setShowCandidatesPanel(false);
    setShowMergeModal(true);
  };
  
  const [form, setForm] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    name: '',
    email: '',
    role: 'user',
    status: 'active',
    use_custom_permissions: false,
    permissions: []
  });

  useEffect(() => {
    loadUsers();
    loadPermissions();
  }, []);

  useEffect(() => {
    if (activeTab === 'deleted' && deletedUsers.length === 0) {
      loadDeletedUsers();
    }
  }, [activeTab]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await api().get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const loadDeletedUsers = async () => {
    setLoadingDeleted(true);
    try {
      const response = await api().get('/users/deleted');
      setDeletedUsers(response.data);
    } catch (error) {
      console.error('Error loading deleted users:', error);
    } finally {
      setLoadingDeleted(false);
    }
  };

  const handleRestoreUser = async (id, username) => {
    try {
      await api().post(`/users/${id}/restore`);
      toast.success(`Usuario "${username}" restaurado`);
      loadDeletedUsers();
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar');
    }
  };

  const handlePermanentDelete = async (id, username) => {
    if (!window.confirm(`¿Eliminar "${username}" PERMANENTEMENTE?\n\nEsta acción NO se puede deshacer.`)) return;
    try {
      await api().delete(`/users/${id}/permanent`);
      toast.success(`Usuario "${username}" eliminado permanentemente`);
      loadDeletedUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleEmptyTrash = async () => {
    if (!window.confirm(`¿VACIAR PAPELERA?\n\nEsto eliminará PERMANENTEMENTE ${deletedUsers.length} usuario(s).\n\nEsta acción NO se puede deshacer.`)) return;
    try {
      const response = await api().delete('/users/permanent-all');
      toast.success(`Papelera vaciada: ${response.data.deleted} usuarios eliminados`);
      loadDeletedUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al vaciar papelera');
    }
  };

  const loadPermissions = async () => {
    try {
      const response = await api().get('/permissions');
      setAllPermissions(response.data.permissions);
      setDefaultPermissions(response.data.default_by_role);
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  };

  // Bulk selection functions
  const selectableUsers = users.filter(u => u.id !== currentUser?.id);
  
  const toggleSelectUser = (userId, e) => {
    e?.stopPropagation?.();
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === selectableUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(selectableUsers.map(u => u.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUsers.size === 0) return;
    
    const justification = window.prompt(
      `¿Estás seguro de eliminar ${selectedUsers.size} usuario(s)?\n\nPor favor, ingresa una justificación (mínimo 10 caracteres):`,
      ''
    );
    
    if (!justification) return;
    if (justification.length < 10) {
      toast.error('La justificación debe tener al menos 10 caracteres');
      return;
    }
    
    setBulkDeleting(true);
    let deleted = 0;
    let errors = [];
    
    try {
      for (const userId of selectedUsers) {
        try {
          await api().delete(`/users/${userId}`);
          deleted++;
        } catch (error) {
          const user = users.find(u => u.id === userId);
          errors.push(user?.username || userId);
        }
      }
      
      if (deleted > 0) {
        toast.success(`${deleted} usuario(s) eliminado(s)`);
      }
      if (errors.length > 0) {
        toast.error(`Error al eliminar: ${errors.join(', ')}`);
      }
      
      setSelectedUsers(new Set());
      loadUsers();
    } catch (error) {
      toast.error('Error al eliminar usuarios');
    } finally {
      setBulkDeleting(false);
    }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setForm({ 
      username: '', 
      password: '', 
      confirmPassword: '',
      name: '', 
      email: '',
      role: 'user', 
      status: 'active',
      use_custom_permissions: false,
      permissions: defaultPermissions['user'] || []
    });
    setIsModalOpen(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setForm({
      username: user.username,
      password: '',
      confirmPassword: '',
      name: user.name,
      email: user.email || '',
      role: user.role,
      status: user.status,
      use_custom_permissions: user.use_custom_permissions || false,
      permissions: user.permissions || defaultPermissions[user.role] || []
    });
    setIsModalOpen(true);
  };

  const handleRoleChange = (newRole) => {
    setForm(prev => ({
      ...prev,
      role: newRole,
      // Reset to default permissions for the new role if not using custom
      permissions: prev.use_custom_permissions ? prev.permissions : (defaultPermissions[newRole] || [])
    }));
  };

  const handleCustomPermissionsToggle = (checked) => {
    setForm(prev => ({
      ...prev,
      use_custom_permissions: checked,
      // If disabling custom, reset to role defaults
      permissions: checked ? prev.permissions : (defaultPermissions[prev.role] || [])
    }));
  };

  const togglePermission = (permKey) => {
    setForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permKey)
        ? prev.permissions.filter(p => p !== permKey)
        : [...prev.permissions, permKey]
    }));
  };

  const handleSave = async () => {
    if (!form.name || !form.username) {
      toast.error('Nombre y usuario son requeridos');
      return;
    }
    if (!editingUser && !form.password) {
      toast.error('La contraseña es requerida para nuevos usuarios');
      return;
    }
    if (form.password && form.password !== form.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setSaving(true);
    try {
      const data = { 
        username: form.username,
        name: form.name,
        email: form.email || null,
        role: form.role,
        status: form.status,
        use_custom_permissions: form.use_custom_permissions,
        permissions: form.use_custom_permissions ? form.permissions : null
      };
      
      if (form.password) {
        data.password = form.password;
      }

      if (editingUser) {
        await api().put(`/users/${editingUser.id}`, data);
        toast.success('Usuario actualizado');
      } else {
        await api().post('/users', data);
        toast.success('Usuario creado');
      }
      setIsModalOpen(false);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (id === currentUser?.id) {
      toast.error('No puedes eliminar tu propio usuario');
      return;
    }
    if (!window.confirm('¿Mover este usuario a la papelera?')) return;
    
    try {
      await api().delete(`/users/${id}`);
      toast.success('Usuario movido a papelera');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleLockUser = async (id, username) => {
    if (id === currentUser?.id) {
      toast.error('No puedes bloquear tu propio usuario');
      return;
    }
    if (!window.confirm(`¿Estás seguro de bloquear la cuenta de ${username}?`)) return;
    
    try {
      await api().post(`/users/${id}/lock`, null, { params: { reason: 'Bloqueado por administrador' } });
      toast.success(`Cuenta de ${username} bloqueada`);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al bloquear');
    }
  };

  const handleUnlockUser = async (id, username) => {
    try {
      await api().post(`/users/${id}/unlock`);
      toast.success(`Cuenta de ${username} desbloqueada`);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desbloquear');
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      superadmin: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      admin: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      supervisor: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      auditor: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      almacenista: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      user: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return colors[role] || colors.user;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Usuarios</h1>
          <p className="text-muted-foreground mt-1">
            Administra los usuarios del sistema y sus permisos
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isSuperAdmin() && (
            <Button
              onClick={() => loadMergeCandidates()}
              variant="outline"
              className="border-purple-500/40 text-purple-500 hover:bg-purple-500/10"
              data-testid="open-merge-candidates-btn"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Candidatos
              {mergeCandidates && mergeCandidates.length > 0 && (
                <Badge className="ml-2" variant="secondary">{mergeCandidates.length}</Badge>
              )}
            </Button>
          )}
          {isSuperAdmin() && (
            <Button
              onClick={() => setShowMergeModal(true)}
              variant="outline"
              className="border-amber-500/40 text-amber-500 hover:bg-amber-500/10"
              data-testid="open-merge-users-btn"
            >
              <GitMerge className="h-4 w-4 mr-2" />
              Fusionar usuarios
            </Button>
          )}
          <Button onClick={openCreateModal} className="btn-primary" data-testid="add-user-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Usuario
          </Button>
        </div>
      </div>

      {/* v5.4.19 — Merge candidates panel */}
      {showCandidatesPanel && (
        <Card className="border-purple-500/40" data-testid="merge-candidates-panel">
          <CardHeader className="pb-3 flex flex-row items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Sparkles className="h-5 w-5 text-purple-500" />
                Candidatos de fusión sugeridos
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Pares detectados automáticamente por similitud (mismo local-part de correo, nombre completo idéntico, etc.).
                Click en &quot;Fusionar&quot; para pre-cargar el flujo manual.
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setShowCandidatesPanel(false)} data-testid="close-candidates-panel">
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {candidatesLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Buscando candidatos...
              </div>
            ) : !mergeCandidates || mergeCandidates.length === 0 ? (
              <div className="text-sm text-muted-foreground p-3 border rounded-lg bg-emerald-500/5 border-emerald-500/30">
                ✓ No se detectaron pares candidatos. Tus usuarios parecen limpios.
              </div>
            ) : (
              <div className="space-y-2">
                {mergeCandidates.map((c, idx) => (
                  <div
                    key={`${c.user_a.id}-${c.user_b.id}`}
                    className="flex items-center justify-between gap-3 p-3 border rounded-lg hover:bg-muted/30"
                    data-testid={`candidate-row-${idx}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <span className="truncate">{c.user_a.full_name || c.user_a.username}</span>
                        <span className="text-muted-foreground">↔</span>
                        <span className="truncate">{c.user_b.full_name || c.user_b.username}</span>
                        <Badge
                          variant={c.score >= 80 ? 'destructive' : c.score >= 60 ? 'default' : 'secondary'}
                          className="ml-2"
                        >
                          {c.score}%
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {(c.user_a.email || '—')} vs {(c.user_b.email || '—')} · {c.reasons.join(' · ')}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => pickCandidate(c)}
                      data-testid={`use-candidate-${idx}`}
                    >
                      <GitMerge className="h-3 w-3 mr-1" /> Fusionar
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2">
        <Button
          variant={activeTab === 'active' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('active')}
        >
          <Shield className="h-4 w-4 mr-2" />
          Usuarios Activos ({users.length})
        </Button>
        {(isSuperAdmin() || currentUser?.role === 'admin') && (
          <Button
            variant={activeTab === 'deleted' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('deleted')}
          >
            <Archive className="h-4 w-4 mr-2" />
            Papelera ({deletedUsers.length})
          </Button>
        )}
      </div>

      {/* Active Users Tab */}
      {activeTab === 'active' && (
        <>
          {/* Bulk Selection Bar */}
          {canBulkDelete && selectableUsers.length > 0 && (
            <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg border">
              <button
                onClick={toggleSelectAll}
                className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors"
              >
                {selectedUsers.size === selectableUsers.length && selectableUsers.length > 0 ? (
                  <CheckSquare className="h-5 w-5 text-primary" />
                ) : (
                  <Square className="h-5 w-5" />
                )}
                {selectedUsers.size === selectableUsers.length && selectableUsers.length > 0 
                  ? 'Deseleccionar todos' 
                  : 'Seleccionar todos'}
              </button>
              
              {selectedUsers.size > 0 && (
                <>
                  <span className="text-sm text-muted-foreground">
                    {selectedUsers.size} usuario(s) seleccionado(s)
                  </span>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                  >
                    {bulkDeleting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    Eliminar seleccionados
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedUsers(new Set())}
                  >
                    Cancelar
                  </Button>
                </>
              )}
            </div>
          )}

          <Card className="card-hover">
            <CardContent className="p-0 overflow-x-auto">
              <Table className="min-w-[800px]">
                <TableHeader>
                  <TableRow className="table-header">
                    {canBulkDelete && <TableHead className="w-12"></TableHead>}
                <TableHead>Usuario</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Autenticación</TableHead>
                <TableHead>Permisos</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Último Acceso</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={canBulkDelete ? 8 : 7}>
                      <Skeleton className="h-12 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canBulkDelete ? 8 : 7} className="text-center py-12">
                    <Shield className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">No hay usuarios</p>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} className="table-row" data-testid={`user-row-${user.id}`}>
                    {canBulkDelete && (
                      <TableCell>
                        {user.id !== currentUser?.id && (
                          <button
                            onClick={(e) => toggleSelectUser(user.id, e)}
                            className="p-1 hover:bg-muted rounded transition-colors"
                          >
                            {selectedUsers.has(user.id) ? (
                              <CheckSquare className="h-5 w-5 text-primary" />
                            ) : (
                              <Square className="h-5 w-5 text-muted-foreground" />
                            )}
                          </button>
                        )}
                      </TableCell>
                    )}
                    <TableCell className="font-mono">{user.username}</TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {user.avatar_url ? (
                          <img src={user.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-primary font-medium text-[10px]">{user.name?.charAt(0)?.toUpperCase()}</span>
                          </div>
                        )}
                        {user.name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getRoleBadgeColor(user.role)}>
                        {getRoleLabel(user.role)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.auth_provider === 'microsoft' ? (
                        <Badge variant="outline" className="text-blue-400 border-blue-400/30 bg-blue-500/10">
                          <svg className="h-3 w-3 mr-1" viewBox="0 0 21 21" fill="none">
                            <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                            <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                            <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                            <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                          </svg>
                          Microsoft
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground border-border/50">
                          Local
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.use_custom_permissions ? (
                        <Badge variant="outline" className="text-primary border-primary/30">
                          <Key className="h-3 w-3 mr-1" />
                          Personalizados
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">Por defecto</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge className={user.status === 'active' ? 'status-disponible' : 'status-baja'}>
                          {user.status === 'active' ? 'Activo' : 'Inactivo'}
                        </Badge>
                        {user.is_locked && (
                          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                            <Lock className="h-3 w-3 mr-1" />
                            Bloqueado
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {user.last_access ? formatDate(user.last_access) : 'Nunca'}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditModal(user)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          {user.is_locked ? (
                            <DropdownMenuItem 
                              onClick={() => handleUnlockUser(user.id, user.username)}
                              className="text-emerald-500"
                            >
                              <Unlock className="h-4 w-4 mr-2" />
                              Desbloquear
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem 
                              onClick={() => handleLockUser(user.id, user.username)}
                              className="text-amber-500"
                              disabled={user.id === currentUser?.id}
                            >
                              <Lock className="h-4 w-4 mr-2" />
                              Bloquear
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            onClick={() => handleDelete(user.id)} 
                            className="text-destructive"
                            disabled={user.id === currentUser?.id}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
        </>
      )}

      {/* Deleted Users Tab (Trash) */}
      {activeTab === 'deleted' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Archive className="h-5 w-5" />
                Usuarios Eliminados
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Usuarios que han sido eliminados y pueden ser restaurados
              </p>
            </div>
            {deletedUsers.length > 0 && (
              <Button variant="destructive" size="sm" onClick={handleEmptyTrash}>
                <Trash2 className="h-4 w-4 mr-2" />
                Vaciar Papelera
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {loadingDeleted ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : deletedUsers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Archive className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No hay usuarios eliminados</p>
              </div>
            ) : (
              <div className="space-y-2">
                {deletedUsers.map(user => (
                  <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50">
                    <div className="flex items-center gap-3">
                      <Shield className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{user.username}</p>
                        <p className="text-sm text-muted-foreground">
                          {user.name} - Eliminado: {user.deleted_at ? new Date(user.deleted_at).toLocaleDateString() : 'Fecha desconocida'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleRestoreUser(user.id, user.username)}
                        className="text-green-500 hover:text-green-400"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Restaurar
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => handlePermanentDelete(user.id, user.username)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
            <DialogDescription>
              {editingUser ? 'Modifica los datos del usuario' : 'Crea un nuevo usuario del sistema'}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="general" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="general">Datos Generales</TabsTrigger>
              <TabsTrigger value="permissions" disabled={!isSuperAdmin()}>
                <Key className="h-4 w-4 mr-2" />
                Permisos
              </TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Usuario *</Label>
                  <Input
                    id="username"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    placeholder="nombre.usuario"
                    data-testid="user-username-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">
                    Contraseña {editingUser ? '(dejar vacío para mantener)' : '*'}
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder="••••••••"
                      className="pr-10"
                      data-testid="user-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">
                    Confirmar Contraseña {editingUser ? '' : '*'}
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={form.confirmPassword}
                      onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                      placeholder="••••••••"
                      className={`pr-10 ${form.password && form.confirmPassword && form.password !== form.confirmPassword ? 'border-red-500' : ''}`}
                      data-testid="user-confirm-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      data-testid="toggle-confirm-password-visibility"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                    </Button>
                  </div>
                  {form.password && form.confirmPassword && form.password !== form.confirmPassword && (
                    <p className="text-xs text-red-500">Las contraseñas no coinciden</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Nombre Completo *</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Nombre completo"
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Correo Electrónico</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="usuario@ejemplo.com"
                  data-testid="user-email-input"
                />
                <p className="text-xs text-muted-foreground">
                  Necesario para recuperar contraseña y recibir notificaciones
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="role">Rol</Label>
                  <Select
                    value={form.role}
                    onValueChange={handleRoleChange}
                  >
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">Usuario</SelectItem>
                      <SelectItem value="almacenista">Almacenista</SelectItem>
                      <SelectItem value="auditor">Auditor</SelectItem>
                      <SelectItem value="supervisor">Supervisor</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                      <SelectItem value="superadmin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Estado</Label>
                  <Select
                    value={form.status}
                    onValueChange={(value) => setForm({ ...form, status: value })}
                  >
                    <SelectTrigger data-testid="user-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Activo</SelectItem>
                      <SelectItem value="inactive">Inactivo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="permissions" className="space-y-4 pt-4">
              {isSuperAdmin() ? (
                <>
                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div className="space-y-0.5">
                      <Label className="text-base">Permisos Personalizados</Label>
                      <p className="text-sm text-muted-foreground">
                        Activa para configurar permisos manualmente en lugar de usar los del rol
                      </p>
                    </div>
                    <Switch
                      checked={form.use_custom_permissions}
                      onCheckedChange={handleCustomPermissionsToggle}
                      data-testid="custom-permissions-switch"
                    />
                  </div>

                  {form.use_custom_permissions ? (
                    <div className="space-y-4">
                      {Object.entries(permissionGroups).map(([group, perms]) => (
                        <Card key={group} className="p-4">
                          <h4 className="font-medium mb-3 text-sm text-primary">{group}</h4>
                          <div className="grid grid-cols-2 gap-2">
                            {perms.map(perm => (
                              <div key={perm} className="flex items-center space-x-2">
                                <Checkbox
                                  id={perm}
                                  checked={form.permissions.includes(perm)}
                                  onCheckedChange={() => togglePermission(perm)}
                                />
                                <Label 
                                  htmlFor={perm} 
                                  className="text-sm font-normal cursor-pointer"
                                >
                                  {permissionLabels[perm] || perm}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Settings className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>El usuario usará los permisos por defecto del rol <strong className="text-foreground">{getRoleLabel(form.role)}</strong></p>
                      <p className="text-sm mt-2">Activa &quot;Permisos Personalizados&quot; para configurar manualmente</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Solo el Super Administrador puede modificar permisos</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-user-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingUser ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v5.4.0 — MERGE USERS MODAL (superadmin only) */}
      <MergeUsersDialog
        open={showMergeModal}
        onOpenChange={(open) => {
          setShowMergeModal(open);
          if (!open) {
            setMergeUserA('');
            setMergeUserB('');
            setMergePrimary('a');
            setMergePreview(null);
            setMergeConfirmText('');
          }
        }}
        users={users}
        currentUserId={currentUser?.id}
        userA={mergeUserA}
        userB={mergeUserB}
        primary={mergePrimary}
        preview={mergePreview}
        loading={mergeLoading}
        executing={mergeExecuting}
        confirmText={mergeConfirmText}
        onChangeA={setMergeUserA}
        onChangeB={setMergeUserB}
        onTogglePrimary={() => setMergePrimary((p) => (p === 'a' ? 'b' : 'a'))}
        onSetPrimary={setMergePrimary}
        onConfirmTextChange={setMergeConfirmText}
        onPreview={async () => {
          if (!mergeUserA || !mergeUserB || mergeUserA === mergeUserB) {
            toast.error('Selecciona dos usuarios diferentes');
            return;
          }
          const primaryId = mergePrimary === 'a' ? mergeUserA : mergeUserB;
          const secondaryId = mergePrimary === 'a' ? mergeUserB : mergeUserA;
          setMergeLoading(true);
          try {
            const res = await api().post('/users/merge/preview', { primary_id: primaryId, secondary_id: secondaryId });
            setMergePreview(res.data);
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'No se pudo generar la vista previa');
            setMergePreview(null);
          } finally {
            setMergeLoading(false);
          }
        }}
        onExecute={async () => {
          const primaryId = mergePrimary === 'a' ? mergeUserA : mergeUserB;
          const secondaryId = mergePrimary === 'a' ? mergeUserB : mergeUserA;
          setMergeExecuting(true);
          try {
            const res = await api().post('/users/merge', {
              primary_id: primaryId,
              secondary_id: secondaryId,
              confirm: mergeConfirmText,
            });
            toast.success(`Fusión completada: ${res.data.total_records} registro(s) transferido(s)`);
            setShowMergeModal(false);
            setMergeUserA('');
            setMergeUserB('');
            setMergePreview(null);
            setMergeConfirmText('');
            loadUsers();
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'No se pudo completar la fusión');
          } finally {
            setMergeExecuting(false);
          }
        }}
      />
    </div>
  );
};

// ============================================================
// v5.4.0 — Merge Users Dialog (extracted for clarity)
// ============================================================
const MergeUsersDialog = ({
  open, onOpenChange, users, currentUserId,
  userA, userB, primary, preview, loading, executing, confirmText,
  onChangeA, onChangeB, onTogglePrimary, onSetPrimary, onConfirmTextChange,
  onPreview, onExecute,
}) => {
  const selectable = (users || []).filter((u) => u.id !== currentUserId);
  const findUser = (id) => selectable.find((u) => u.id === id);
  const a = findUser(userA);
  const b = findUser(userB);
  const primaryUser = primary === 'a' ? a : b;
  const secondaryUser = primary === 'a' ? b : a;
  const canPreview = !!a && !!b && a.id !== b.id && !loading;
  const canExecute = !!preview && confirmText === 'FUSIONAR' && !executing;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="merge-users-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-amber-500" />
            Fusionar usuarios
          </DialogTitle>
          <DialogDescription>
            Combina dos cuentas en una sola. Todos los registros del usuario secundario se transfieren al primario.
            Ambos métodos de login (Microsoft SSO y usuario/contraseña) seguirán funcionando.
          </DialogDescription>
        </DialogHeader>

        {/* Step 1 — Select users */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-end">
            <div>
              <Label>Usuario A</Label>
              <Select value={userA} onValueChange={onChangeA}>
                <SelectTrigger data-testid="merge-user-a-select">
                  <SelectValue placeholder="Selecciona un usuario" />
                </SelectTrigger>
                <SelectContent>
                  {selectable.filter((u) => u.id !== userB).map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} ({u.username}) {u.auth_provider === 'microsoft' ? ' · MS' : u.auth_provider === 'hybrid' ? ' · Híbrido' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-center pb-2">
              <ArrowLeftRight className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <Label>Usuario B</Label>
              <Select value={userB} onValueChange={onChangeB}>
                <SelectTrigger data-testid="merge-user-b-select">
                  <SelectValue placeholder="Selecciona un usuario" />
                </SelectTrigger>
                <SelectContent>
                  {selectable.filter((u) => u.id !== userA).map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} ({u.username}) {u.auth_provider === 'microsoft' ? ' · MS' : u.auth_provider === 'hybrid' ? ' · Híbrido' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {a && b && (
            <div className="rounded-lg border bg-muted/30 p-3 space-y-3">
              <div className="text-sm font-medium text-muted-foreground">¿Cuál cuenta debe sobrevivir? (la otra será absorbida)</div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => onSetPrimary('a')}
                  className={`text-left p-3 rounded-md border-2 transition-all ${primary === 'a' ? 'border-emerald-500 bg-emerald-500/10' : 'border-border hover:border-muted-foreground'}`}
                  data-testid="merge-pick-primary-a"
                >
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{primary === 'a' ? '✓ Primario' : 'Secundario'}</div>
                  <div className="font-medium">{a.name}</div>
                  <div className="text-xs text-muted-foreground">@{a.username} · {a.email || 'sin correo'}</div>
                </button>
                <button
                  type="button"
                  onClick={() => onSetPrimary('b')}
                  className={`text-left p-3 rounded-md border-2 transition-all ${primary === 'b' ? 'border-emerald-500 bg-emerald-500/10' : 'border-border hover:border-muted-foreground'}`}
                  data-testid="merge-pick-primary-b"
                >
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{primary === 'b' ? '✓ Primario' : 'Secundario'}</div>
                  <div className="font-medium">{b.name}</div>
                  <div className="text-xs text-muted-foreground">@{b.username} · {b.email || 'sin correo'}</div>
                </button>
              </div>
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onTogglePrimary}
                  className="text-xs"
                  data-testid="merge-swap-primary-btn"
                >
                  <ArrowLeftRight className="h-3 w-3 mr-1" />
                  Invertir
                </Button>
              </div>
            </div>
          )}

          <Button
            onClick={onPreview}
            disabled={!canPreview}
            variant="secondary"
            className="w-full"
            data-testid="merge-preview-btn"
          >
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Generar vista previa
          </Button>
        </div>

        {/* Step 2 — Preview */}
        {preview && (
          <div className="space-y-4 pt-4 border-t" data-testid="merge-preview-panel">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border-2 border-emerald-500 bg-emerald-500/5 p-3">
                <div className="text-xs uppercase tracking-wider text-emerald-600 dark:text-emerald-400 font-semibold">Cuenta que sobrevive</div>
                <div className="font-medium mt-1">{primaryUser?.name}</div>
                <div className="text-xs text-muted-foreground">@{preview.primary.username}</div>
                <div className="text-xs text-muted-foreground">{preview.primary.email}</div>
              </div>
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                <div className="text-xs uppercase tracking-wider text-destructive font-semibold">Cuenta que se absorbe</div>
                <div className="font-medium mt-1 line-through">{secondaryUser?.name}</div>
                <div className="text-xs text-muted-foreground line-through">@{preview.secondary.username}</div>
                <div className="text-xs text-muted-foreground line-through">{preview.secondary.email}</div>
              </div>
            </div>

            <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
              <div className="text-sm font-semibold">Alias resultantes en la cuenta primaria</div>
              <div className="flex flex-wrap gap-1">
                {(preview.result_aliases?.usernames || []).map((u) => (
                  <Badge key={`u-${u}`} variant="outline" className="text-xs">@{u}</Badge>
                ))}
              </div>
              <div className="flex flex-wrap gap-1">
                {(preview.result_aliases?.emails || []).map((e) => (
                  <Badge key={`e-${e}`} variant="outline" className="text-xs">{e}</Badge>
                ))}
              </div>
              <div className="text-xs text-muted-foreground pt-1">
                El usuario podrá iniciar sesión con cualquiera de estos usernames y recuperar su contraseña con cualquiera de estos correos.
              </div>
            </div>

            <div className="rounded-lg border p-3">
              <div className="text-sm font-semibold mb-2">Registros que se moverán: <span className="text-primary">{preview.total_records}</span></div>
              {preview.total_records === 0 ? (
                <p className="text-xs text-muted-foreground">El usuario secundario no tiene registros asociados. La fusión solo unifica las cuentas de login.</p>
              ) : (
                <div className="grid grid-cols-2 gap-1 text-xs max-h-40 overflow-y-auto">
                  {Object.entries(preview.record_counts || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between bg-muted/40 rounded px-2 py-1">
                      <span className="text-muted-foreground truncate">{k}</span>
                      <span className="font-mono font-medium">{v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-lg border-2 border-amber-500/40 bg-amber-500/5 p-3 space-y-2">
              <div className="text-sm font-semibold text-amber-700 dark:text-amber-400">⚠️ Esta acción es definitiva</div>
              <p className="text-xs text-muted-foreground">
                Para confirmar, escribe <code className="bg-muted px-1 rounded font-mono">FUSIONAR</code> abajo.
              </p>
              <Input
                value={confirmText}
                onChange={(e) => onConfirmTextChange(e.target.value)}
                placeholder="Escribe FUSIONAR para confirmar"
                data-testid="merge-confirm-input"
                className="font-mono"
              />
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="merge-cancel-btn">
            Cancelar
          </Button>
          <Button
            onClick={onExecute}
            disabled={!canExecute}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="merge-execute-btn"
          >
            {executing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <GitMerge className="h-4 w-4 mr-2" />
            Fusionar cuentas
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UsersPage;
